import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, Inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { UserActionIds, UserActionTrackerService } from '@miq/utils/metrics';
import { MessageFunctionType } from './digital-assistant.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable()
export class DigitalAssistantService {
  constructor(
    private httpClient: HttpClient,
    private userActionTrackerService: UserActionTrackerService,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  public isLeftPanelOpen = signal(false);

  public isPromptsPanelOpen = signal(false);

  public isPredefinedPromptSelected = signal(false);
  public selectedPredefinedPrompt = signal('');

  public headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  });

  // Function to send a message to the backend
  sendMessage(
    request: Record<string, any>,
    chatHistory?: () => Record<string, any>[]
  ): Observable<Record<string, any>> {
    const url = '/research-assistant';
    const messagesArray = chatHistory ? chatHistory() : [];

    const conversationHistory = messagesArray
      .filter(message => message.type !== MessageFunctionType.Loading)
      .map(message => ({
        id: message.id,
        from: message.from,
        type: message.type,
        content: message.type === 'text' ? message.content : message.content.summary || '',
      }));

    const actionDetailParams: Record<string, any> = {
      request: {
        requestId: request.request_id,
        userInput: request.user_input,
        convHistory: conversationHistory,
      },
    };

    if (this.isPredefinedPromptSelected()) {
      actionDetailParams.selectedPrompt = this.selectedPredefinedPrompt();
    }

    this.trackUserAction('Sending Message', actionDetailParams);

    return this.httpClient.post<Record<string, any>>(url, request, { headers: this.headers }).pipe(
      catchError(error => {
        this.logger.error(`Error sending message: ${error}`);
        return throwError(() => error);
      })
    );
  }

  trackUserAction(actionTaken: string, actionDetailParams?: Record<string, any>): void {
    const actionId = UserActionIds.DigitalAssistant;
    const actionDetails: Record<string, any> = {
      actionId,
      actionTaken: actionTaken,
      ...actionDetailParams,
    };
    this.userActionTrackerService.registerAction({ actionId, actionDetails });
  }
}
